import { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

export default function VotingCreatorTools({ postId, isCreator }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [showTools, setShowTools] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [campaignData, setCampaignData] = useState({
    title: "",
    description: "",
    content: "",
    targetOption: "",
  });
  const [sending, setSending] = useState(false);
  const [postSettings, setPostSettings] = useState({
    resultsVisible: true,
    isAnonymous: false,
    allowMultiple: false,
    startDate: "",
    isPaid: false,
    entryFee: 0,
  });

  const fetchPostSettings = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const vd = data.votingData || {};
      setPostSettings({
        resultsVisible: vd.resultsVisible !== false,
        isAnonymous: !!vd.isAnonymous,
        allowMultiple: !!vd.allowMultiple,
        startDate: vd.startDate ? new Date(vd.startDate).toISOString().slice(0,16) : "",
        isPaid: !!vd.isPaid,
        entryFee: vd.entryFee || 0,
      });
    } catch (e) {
      console.error("Erro ao obter configurações da votação", e);
    }
  };

  const toggleTools = async () => {
    const next = !showTools;
    setShowTools(next);
    if (next) {
      await fetchPostSettings();
    }
  };

  if (!isCreator) return null;

  const handleSendNotification = async () => {
    if (!notificationMessage.trim()) {
      alert("Digite uma mensagem");
      return;
    }

    try {
      setSending(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/notifications`,
        {
          type: "voting_reminder",
          message: notificationMessage,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      alert("Notificação enviada aos seguidores!");
      setNotificationMessage("");
      setSending(false);
    } catch (error) {
      console.error("Error sending notification:", error);
      alert("Erro ao enviar notificação");
      setSending(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignData.title.trim()) {
      alert("Digite um título para a campanha");
      return;
    }

    try {
      setSending(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/campaigns`,
        campaignData,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      alert("Campanha criada com sucesso!");
      setCampaignData({
        title: "",
        description: "",
        content: "",
        targetOption: "",
      });
      setSending(false);
    } catch (error) {
      console.error("Error creating campaign:", error);
      alert("Erro ao criar campanha");
      setSending(false);
    }
  };

  const handleToggleResultsVisibility = async () => {
    try {
      setSending(true);
      const newValue = !postSettings.resultsVisible;
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/settings`,
        { resultsVisible: newValue },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setPostSettings((prev) => ({ ...prev, resultsVisible: newValue }));
      setSending(false);
    } catch (error) {
      console.error("Erro ao atualizar visibilidade de resultados:", error);
      alert("Não foi possível atualizar a visibilidade");
      setSending(false);
    }
  };

  const handleToggleAnonymity = async () => {
    try {
      setSending(true);
      const newValue = !postSettings.isAnonymous;
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/settings`,
        { isAnonymous: newValue },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setPostSettings((prev) => ({ ...prev, isAnonymous: newValue }));
      setSending(false);
    } catch (error) {
      console.error("Erro ao atualizar anonimato:", error);
      alert("Não foi possível atualizar o anonimato");
      setSending(false);
    }
  };

  const handleToggleAllowMultiple = async () => {
    try {
      setSending(true);
      const newValue = !postSettings.allowMultiple;
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/settings`,
        { allowMultiple: newValue },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setPostSettings((prev) => ({ ...prev, allowMultiple: newValue }));
      setSending(false);
    } catch (error) {
      console.error("Erro ao atualizar múltipla escolha:", error);
      alert("Não foi possível atualizar a configuração");
      setSending(false);
    }
  };

  const handleScheduleStart = async () => {
    try {
      if (!postSettings.startDate) {
        alert("Selecione uma data/hora de início");
        return;
      }
      setSending(true);
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/settings`,
        { startDate: postSettings.startDate },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      alert("Início agendado atualizado");
      setSending(false);
    } catch (error) {
      console.error("Erro ao agendar início:", error);
      alert("Não foi possível agendar o início");
      setSending(false);
    }
  };

  const handleCloseVoting = async () => {
    try {
      const proceed = typeof window !== "undefined" && window.confirm
        ? window.confirm("Deseja encerrar esta votação agora? Esta ação é irreversível.")
        : true;
      if (!proceed) return;
      setSending(true);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/close`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      alert("Votação encerrada");
      setSending(false);
    } catch (error) {
      console.error("Erro ao encerrar votação:", error);
      alert("Não foi possível encerrar a votação");
      setSending(false);
    }
  };

  const handleTogglePaid = async () => {
    try {
      setSending(true);
      const newValue = !postSettings.isPaid;
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/settings`,
        { isPaid: newValue },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setPostSettings((prev) => ({ ...prev, isPaid: newValue }));
      setSending(false);
    } catch (error) {
      console.error("Erro ao alternar votação paga:", error);
      alert("Não foi possível atualizar a configuração");
      setSending(false);
    }
  };

  const handleUpdateEntryFee = async () => {
    try {
      setSending(true);
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/settings`,
        { entryFee: Number(postSettings.entryFee || 0), feeCurrency: "BRL" },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      alert("Taxa atualizada");
      setSending(false);
    } catch (error) {
      console.error("Erro ao atualizar taxa:", error);
      alert("Não foi possível atualizar a taxa");
      setSending(false);
    }
  };

  return (
    <div className="voting_creator_tools">
      <button
        className="voting_creator_toggle"
        onClick={toggleTools}
      >
        ⚙️ Ferramentas do Criador
      </button>

      {showTools && (
        <div className="voting_creator_panel">
          {/* Enviar Notificação */}
          <div className="creator_tool_section">
            <h4>📢 Enviar Notificação</h4>
            <textarea
              placeholder="Digite a mensagem para os seguidores..."
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              rows={3}
            />
            <button onClick={handleSendNotification} disabled={sending}>
              {sending ? "Enviando..." : "Enviar Notificação"}
            </button>
          </div>

          {/* Criar Campanha */}
          <div className="creator_tool_section">
            <h4>🗳️ Criar Campanha</h4>
            <input
              type="text"
              placeholder="Título da campanha"
              value={campaignData.title}
              onChange={(e) =>
                setCampaignData({ ...campaignData, title: e.target.value })
              }
            />
            <input
              type="text"
              placeholder="Descrição breve"
              value={campaignData.description}
              onChange={(e) =>
                setCampaignData({ ...campaignData, description: e.target.value })
              }
            />
            <textarea
              placeholder="Conteúdo da campanha (detalhes completos)"
              value={campaignData.content}
              onChange={(e) =>
                setCampaignData({ ...campaignData, content: e.target.value })
              }
              rows={4}
            />
            <input
              type="number"
              placeholder="Opção alvo (deixe vazio para geral)"
              value={campaignData.targetOption}
              onChange={(e) =>
                setCampaignData({ ...campaignData, targetOption: e.target.value })
              }
            />
            <button onClick={handleCreateCampaign} disabled={sending}>
              {sending ? "Criando..." : "Criar Campanha"}
            </button>
          </div>

          {/* Configurações da Votação */}
          <div className="creator_tool_section">
            <h4>⚙️ Configurações da Votação</h4>
            <div className="settings_row">
              <span>Mostrar resultados antes do fim</span>
              <button onClick={handleToggleResultsVisibility} disabled={sending}>
                {postSettings.resultsVisible ? "Desativar" : "Ativar"}
              </button>
            </div>
            <div className="settings_row">
              <span>Votação anônima</span>
              <button onClick={handleToggleAnonymity} disabled={sending}>
                {postSettings.isAnonymous ? "Desativar" : "Ativar"}
              </button>
            </div>
            <div className="settings_row">
              <span>Permitir múltiplas escolhas</span>
              <button onClick={handleToggleAllowMultiple} disabled={sending}>
                {postSettings.allowMultiple ? "Desativar" : "Ativar"}
              </button>
            </div>
            <div className="settings_row">
              <span>Votação paga</span>
              <button onClick={handleTogglePaid} disabled={sending}>
                {postSettings.isPaid ? "Desativar" : "Ativar"}
              </button>
            </div>
            {postSettings.isPaid && (
              <div className="settings_row">
                <span>Taxa por voto (BRL)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={postSettings.entryFee}
                  onChange={(e) => setPostSettings((prev) => ({ ...prev, entryFee: e.target.value }))}
                />
                <button onClick={handleUpdateEntryFee} disabled={sending}>Salvar</button>
              </div>
            )}
            <div className="settings_row">
              <span>Agendar início</span>
              <input
                type="datetime-local"
                value={postSettings.startDate}
                onChange={(e) => setPostSettings((prev) => ({ ...prev, startDate: e.target.value }))}
              />
              <button onClick={handleScheduleStart} disabled={sending}>Salvar</button>
            </div>
            <div className="settings_row">
              <span>Encerrar votação</span>
              <button className="danger" onClick={handleCloseVoting} disabled={sending}>Encerrar agora</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
